import { useState, useEffect, memo, useRef } from 'react';
import { IAppointment, IPayment } from '../../../API/GoMoto';
import { AccountType } from '../../../API/Enums';
import { AppointmentDispatchTypes, useAppointmentDispatch, useAppointmentState, useAppointmentUpdateAndPersist } from '../../../context/Appointment/appointment';
import { useKioskImage } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { PaymentError } from '../../../context/mzero';
import { useHideKioskBackground } from '../../../context/Page/page';
import { DataBox, DataBoxItem } from '../../../controls/DataBox/DataBox';
import { Heading } from '../../../controls/Heading/Heading';
import { KioskImageDisplay } from '../../../controls/KioskImageDisplay';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { formatAsCurrency } from '../../../helpers/Number';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import { textCapitalize } from '../../../helpers/textCapitalize';
import { useDisplayLocalizedVehicle } from '../../../helpers/displayVehicle';
import css from './Payment.module.scss';
import { useCardReader } from '../../../context/hardware';
import { GMDialogError } from '../../../controls/Dialog';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { CTAButton } from '../../../controls/ButtonBase';
import { GoMotoComponent, GoMotoFunction } from '../../../API/GoMoto.types';
import { PaymentDialog, PaymentProps } from './Payment.types';
import { LocalizationType } from '../../../context/Localization/localization.types';

/**
 * This component handles the process of taking customer payment during vehicle pick-up
 * if the Kiosk has the ability for a customer to swipe or insert their card into a card reader.
 *
 * @param onSkip - {@link PaymentProps.onSkip onSkip} description.
 * @param onProceed - {@link PaymentProps.onProceed onProceed} description.
 */
export const Payment: GoMotoComponent<PaymentProps> = memo(({
    onSkip,
    onProceed,
}) => {
    useHideKioskBackground()();
    const Localization: LocalizationType = useLocalization();
    const payment = useCardReader();
    const appointment: IAppointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const paymentPopImg: string = useKioskImage('PaymentPop');
    const appointmentDispatch = useAppointmentDispatch();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();

    const [error, setError] = useState<PaymentError | undefined>(payment.error);
    const [dialog, setDialog] = useState<PaymentDialog | undefined>(undefined);
    const [showError, setShowError] = useState<boolean>(false);
    const [cardReaderEnabled, setCardReaderEnabled] = useState<boolean | undefined>();
    const [updating, setUpdating] = useState<boolean>(false);

    const mounted = useRef<boolean>(true);

    useEffect(() => () => {
        mounted.current = false;
    }, []);

    useEffect(() => {
        if (cardReaderEnabled === undefined) {
            payment.cardReaderEnabled.then(setCardReaderEnabled);
        }
        else if (!cardReaderEnabled) {
            onSkip();
        }
    }, [cardReaderEnabled, onSkip, payment.cardReaderEnabled]);

    useEffect(() => {
        if (!payment.loading && !error) {
            setError(payment.error);
        }
    }, [payment, setError, error]);

    useEffect(() => {
        if (!appointment.serviceTotal && !error) {
            setError(PaymentError.valueTooSmall);
        }
    }, [appointment.serviceTotal, error]);

    useEffect(() => {
        if (error) {
            if (error === undefined || typeof error === 'string') {
                setDialog({
                    title: Localization.PaymentError,
                    subHeading: error,
                });
            }
            else {
                switch (error) {
                    case PaymentError.valueTooSmall:
                        setDialog({
                            title: Localization.PaymentError,
                            subHeading: Localization.NoTotalAssigned,
                        });
                        break;
                    case PaymentError.declined:
                        setDialog({
                            title: Localization.PaymentDeclined,
                            subHeading: Localization.PaymentDeclinedFull + ' ' + Localization.PleaseInsertANewCard,
                        });
                        break;
                    case PaymentError.partialApproval:
                        setDialog({
                            title: Localization.PaymentDeclined,
                            subHeading: Localization.PaymentErrorBalance + ' ' + Localization.PleaseInsertANewCard,
                        });
                        break;
                    case PaymentError.voiceReferral:
                        setDialog({
                            title: Localization.PaymentDeclined,
                            subHeading: Localization.PaymentErrorVoiceReferral,
                        });
                        break;
                    case PaymentError.invalidValue:
                        setDialog({
                            title: Localization.PaymentError,
                            heading: Localization.UnableToCompleteTransaction + ' ' + Localization.InvalidValueEntered,
                            subHeading: Localization.PleaseRetryYourCreditCard,
                        });
                        break;
                    case PaymentError.valueTooLarge:
                        setDialog({
                            title: Localization.PaymentError,
                            heading: Localization.UnableToCompleteTransaction + ' ' + Localization.EnteredAmountIsToreThanDeviceAllowedAmount,
                            subHeading: Localization.PleaseRetryYourCreditCard,
                        });
                        break;
                    case PaymentError.timeout:
                        setDialog({
                            title: Localization.PaymentError,
                            heading: Localization.PleaseRetryYourCreditCard,
                        });
                        break;
                    case PaymentError.serviceInUse:
                        setDialog({
                            title: Localization.PaymentError,
                            heading: Localization.PleaseRetryYourCreditCard,
                        });
                        break;
                    default:
                        setDialog({
                            title: Localization.UnexpectedError,
                            heading: Localization.ServiceUnavailable,
                        });
                        break;
                }
            }
        }
        else {
            setDialog(undefined);
        }
    }, [Localization, error]);

    useEffect(() => {
        setShowError(!!dialog);
    }, [dialog, setShowError]);

    useEffect(() => {
        const updatePayment: GoMotoFunction<[void], Promise<void>> = async () => {
            const [{ ApprovalCode, PanMasked, CardScheme, MerchantName, MerchantIdMasked, StatusMessage, TerminalIdMasked, TransactionTime }] = payment.results;

            const last4: string = !!PanMasked ? PanMasked.slice(-4) : '';

            const paymentFields: IPayment = {
                paymentApprovalCode: ApprovalCode,
                paymentCardNumber: PanMasked,
                paymentCardType: CardScheme,
                paymentDateTime: TransactionTime,
                paymentMerchant: MerchantName,
                paymentMerchantId: MerchantIdMasked,
                paymentMessage: StatusMessage,
                paymentTransactionId: TerminalIdMasked,
                paymentMethod: `${CardScheme} ****${last4}`.toUpperCase(),
            };

            await setAppointment({ ...appointment,
                ...paymentFields,
                paymentStatus: 'paid' }, false, false, false, false)
                .then((appt: IAppointment | null) => {
                    if (mounted.current && appt) {
                        appointmentDispatch({ type: AppointmentDispatchTypes.Update,
                            payload: appt });
                    }
                })
                .finally(() => {
                    if (mounted.current) {
                        setUpdating(false);
                    }
                });
        };

        if (!updating && appointment.paymentStatus !== 'paid' && payment.results.length && !payment.error) {
            setUpdating(true);
            updatePayment();
        }
    }, [appointment, appointmentDispatch, payment.error, payment.results, setAppointment, updating]);

    const reset: VoidFunction = () => {
        setError(undefined);
        payment.reset();
    };

    const startPayment: VoidFunction = () => {
        if (appointment.serviceTotal && !payment.loading) {
            payment.pay(appointment.serviceTotal);
        }
    };

    const getDataItems: GoMotoFunction<[void], DataBoxItem[]> = () => {
        const dataItems: DataBoxItem[] = [
            {
                title: Localization.Vehicle,
                value: displayLocalizedVehicle(appointment)
                    .toString(),
                case: 'capitalize',
            },
            'spacer',
            {
                title: Localization.Total,
                value: formatAsCurrency(appointment.serviceTotal || 0, true),
            },
        ];

        if (appointment.accountType === AccountType.business) {
            return [
                {
                    title: Localization.Business,
                    value: textCapitalize(appointment.businessName, true),
                },
                ...dataItems,
            ] as DataBoxItem[];
        }
        else {
            return [
                { title: Localization.FirstName,
                    value: textCapitalize(appointment.firstName) },
                { title: Localization.LastName,
                    value: textCapitalize(appointment.lastName) },
                ...dataItems,
            ] as DataBoxItem[];
        }
    };

    if (!cardReaderEnabled) {
        return (
            <Spinner />
        );
    }
    else {
        return (
            <ScrollableBody
                heading={
                    <Heading
                        primaryHeading={Localization.PayForService}
                        secondaryHeading={Localization.PayForServiceInstructions}
                        roundedIcon={
                            <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                                <GMSizedIcon iconType={'Key'} iconSize={'large'} />
                            </GMRoundedIcon>
                        }
                        showMustache
                    />
                }
                proceedButtons={!payment.loading && cardReaderEnabled ? (
                    (payment.results.length && !payment.error ? (
                        <GMButtonGroup
                            buttons={[
                                <CTAButton
                                    disabled={updating || appointment.paymentStatus !== 'paid'}
                                    onClick={() => onProceed(appointment)}
                                    styleType={'primary'}
                                    title={Localization.ClickToProceedToNextStep}
                                    label={Localization.Proceed}
                                    id={Localization.ClickToProceedToNextStep}
                                    ariaLabel={Localization.ClickToProceedToNextStep}
                                />,
                            ]}
                        />
                    ) : (
                        <GMButtonGroup
                            buttons={[
                                <CTAButton
                                    onClick={startPayment}
                                    styleType={'primary'}
                                    label={Localization.PayNow}
                                    id={Localization.PayNow}
                                    ariaLabel={Localization.PayNow}
                                />,
                            ]}
                        />
                    ))
                ) : undefined}
            >
                <GMDialogError
                    ariaLabel={Localization.AnErrorHasOccured}
                    open={showError}
                    title={dialog && dialog.title ? dialog.title : undefined}
                    onClose={reset}
                >
                    {dialog && dialog.heading ? (
                        <h3>
                            {dialog.heading}
                        </h3>
                    ) : null}
                    {dialog && dialog.subHeading ? (
                        <h3>
                            {dialog.subHeading}
                        </h3>
                    ) : null}
                </GMDialogError>
                {payment.loading || cardReaderEnabled === undefined ? (
                    <Spinner />
                ) : null}
                <KioskImageDisplay
                    left={{ image: paymentPopImg,
                        className: css.paymentLeft }}
                    right={{
                        className: css.paymentRight,
                        component: (
                            <div className={css.container}>
                                <div className={css.top}>
                                    {Localization.SwipeOrInsertCard}
                                </div>
                                <div className={css.dataBox}>
                                    <DataBox
                                        className={css.paymentFormatting}
                                        dataItems={getDataItems()}
                                    />
                                </div>
                                <div className={css.bottom}>
                                    {Localization.FollowInstructionsOnPINPad}
                                </div>
                            </div>
                        ),
                    }}
                />
            </ScrollableBody>
        );
    }
});
